import type {
  MemberReportAccess,
  MemberReportData,
  MemberReportQuery,
} from '@todo/shared';
import { api, get } from './client';

/**
 * Member Report client.
 *
 * Every call goes to THIS app's API proxy (VITE_MEMBER_REPORT_API_URL, e.g.
 * /api/member-report), which holds the admin service token. Nothing here
 * sends or knows a token other than the person's own login, which the shared
 * client attaches.
 */

/** No trailing slash. Empty = not configured; the screen says so. */
export const MEMBER_REPORT_API_URL: string = (
  (import.meta.env.VITE_MEMBER_REPORT_API_URL as string | undefined) ?? ''
).trim().replace(/\/+$/, '');

/** What the screen can show for each failure the report API documents. */
export class MemberReportError extends Error {
  readonly status: number;
  /** 429 - never retried automatically. */
  readonly tooMany: boolean;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'MemberReportError';
    this.status = status;
    this.tooMany = status === 429;
  }
}

const NOT_AUTHORISED = 'Report service is not authorised. Contact the administrator.';

/** HTTP status + the API's own `sts` -> the sentence the screen shows. */
function messageFor(status: number, body: unknown): string {
  const msg = typeof (body as { msg?: unknown } | null)?.msg === 'string'
    ? (body as { msg: string }).msg
    : '';
  // 502 is how the proxy relays an upstream 401 (see member-report.service).
  if (status === 401 || status === 403 || (status === 502 && /token/i.test(msg))) return NOT_AUTHORISED;
  if (status === 429) return 'Too many requests, try again in a minute';
  if (status === 503) return 'The report service is not configured on the server.';
  if (status === 502) return 'The report service is unreachable. Try again.';
  if (status >= 500) return 'The report could not be loaded. Try again.';
  return msg || 'The report could not be loaded.';
}

function clean(query: MemberReportQuery): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(query)) {
    // Empty params are left out entirely: `state=` would filter on "".
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  }
  return out;
}

function requireBase(): string {
  if (!MEMBER_REPORT_API_URL) {
    throw new MemberReportError(
      0,
      'Member report is not configured. Set VITE_MEMBER_REPORT_API_URL in the web .env.',
    );
  }
  return MEMBER_REPORT_API_URL;
}

/** True for HTTP 200 + sts "0". `sts` can be a string or a number. */
function succeeded(status: number, body: unknown): boolean {
  // eslint-disable-next-line eqeqeq
  return status === 200 && (body as { sts?: unknown } | null)?.sts == 0;
}

/**
 * validateStatus lets every answer through EXCEPT a 401. A 401 here is this
 * app's own session expiring (the proxy relays an upstream 401 as 502), so it
 * must reach the shared client's refresh-and-replay.
 */
const passThrough = (status: number) => status !== 401;

export const memberReportApi = {
  /** GET /api/member-report/access - is the section on for this person? */
  async access(): Promise<boolean> {
    return (await get<MemberReportAccess>('/member-report/access')).enabled;
  },

  /** GET .../constituency - drives every card from ONE request. */
  async report(query: MemberReportQuery, signal?: AbortSignal): Promise<MemberReportData> {
    const res = await api.get(`${requireBase()}/constituency`, {
      baseURL: '',
      params: clean(query),
      signal,
      validateStatus: passThrough,
    });
    if (!succeeded(res.status, res.data)) {
      throw new MemberReportError(res.status, messageFor(res.status, res.data));
    }
    return (res.data as { data: MemberReportData }).data;
  },

  /** GET .../pdf - the same filters as the screen, saved as a file. */
  async downloadPdf(query: MemberReportQuery): Promise<void> {
    const res = await api.get<Blob>(`${requireBase()}/pdf`, {
      baseURL: '',
      params: clean(query),
      responseType: 'blob',
      validateStatus: passThrough,
    });

    const blob = res.data;
    if (res.status !== 200 || !blob.type.includes('application/pdf')) {
      // Not a PDF: it is the API's JSON error. Read it and show its `msg`.
      let body: unknown = null;
      try { body = JSON.parse(await blob.text()); } catch { /* not JSON */ }
      throw new MemberReportError(res.status, messageFor(res.status, body));
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'member-constituency-report.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  /** The open geo lists, cached for the session (120 calls a minute limit). */
  async geo(
    kind: 'states' | 'districts' | 'constituencies',
    params: { state_name?: string; district_name?: string } = {},
  ): Promise<string[]> {
    const cacheKey = `${kind}|${params.state_name ?? ''}|${params.district_name ?? ''}`;
    const hit = geoCache.get(cacheKey);
    if (hit) return hit;

    const res = await api.get(`${requireBase()}/geo/${kind}`, {
      baseURL: '',
      params: clean(params as MemberReportQuery),
      validateStatus: passThrough,
    });
    if (!succeeded(res.status, res.data)) {
      throw new MemberReportError(res.status, messageFor(res.status, res.data));
    }
    const field = kind === 'states' ? 'state_name' : kind === 'districts' ? 'district_name' : 'constituency_name';
    const rows = (res.data as { data?: Array<Record<string, unknown>> }).data ?? [];
    // Each row carries its own state_name / district_name. Narrow on them here
    // as well, so the lists cascade (state -> districts -> constituencies) even
    // if the upstream ignores a filter param.
    const same = (a: unknown, b: string) =>
      typeof a === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase();
    const scoped = rows.filter((r) =>
      (!params.state_name || !('state_name' in r) || same(r.state_name, params.state_name)) &&
      (!params.district_name || !('district_name' in r) || same(r.district_name, params.district_name)));
    const names = [...new Set(scoped.map((r) => r[field]).filter((v): v is string => typeof v === 'string' && v !== ''))]
      .sort((a, b) => a.localeCompare(b));
    geoCache.set(cacheKey, names);
    return names;
  },
};

const geoCache = new Map<string, string[]>();
